import type { Asset } from '@schibsted-svp/svp-api-types';
import type { StreamProvider } from '@vgtv/api-client';

import type { CastingType } from '../hooks/CastProvider';

const cleanupObject = <T extends { [k: string]: unknown }>(obj: T) =>
  Object.entries(obj)
    .filter(([, value]) => value !== undefined)
    .reduce((aggr, [k, v]) => ({ ...aggr, [k]: v }), {});

export interface ButtonTarget {
  '@id'?: string;
  '@type'?: string;
  'elementType'?: string;
  'name'?: string;
  'url'?: string;
}

interface ButtonObject {
  id?: string;
  name: string;
  elementType?: string;
  assetId?: number;
  adId?: string;
  objectType?: 'ad' | 'asset';
}

export const createOriginDecorator =
  (provider: StreamProvider | 'brandstudio', asset: Partial<Asset> = {}) =>
  (event: Record<string, unknown>) => {
    return {
      ...event,
      origin: {
        '@id': `sdrn:${asset.provider ?? provider}:mediaasset:${asset.id}`,
        '@type': 'MediaAsset',
        'name': asset.title,
      },
    };
  };

export const createButtonClickEvent = ({
  provider,
  target,
  object,
  intent = 'Click',
  sptCustom,
}: {
  provider: StreamProvider | 'brandstudio';
  target?: ButtonTarget;
  object: ButtonObject;
  intent?: string;
  sptCustom?: Partial<Record<string, unknown>>;
}) => {
  const id =
    object.objectType === 'ad'
      ? `sdrn:${provider}:mediaad:${object.adId}:element:${object.id || object.name}`
      : `sdrn:${provider}:mediaasset:${object.assetId}:element:${object.id || object.name}`;
  return {
    '@type': 'Engagement',
    'action': 'Click',
    'intent': intent,
    ...(target && { target }),
    'object': {
      '@id': id,
      '@type': 'UIElement',
      'mediaAssetId': object.assetId,
      'elementType': object.elementType ?? 'button',
      'name': object.name,
      ...(sptCustom && { 'spt:custom': sptCustom }),
    },
  };
};

export const createViewAdEvent = (
  provider: StreamProvider | 'brandstudio',
  asset: Partial<Asset> = {}
) => {
  return createOriginDecorator(
    provider,
    asset
  )({
    '@type': 'View',
    'object': {
      '@id': `sdrn:${provider}:content:plugin:element:skip-ad`,
      '@type': 'UIElement',
      'name': 'ad view',
    },
  });
};

export const createViewErrorEvent = (
  provider: StreamProvider | 'brandstudio',
  asset: Partial<Asset> = {},
  errorCode = 0
) => {
  return createOriginDecorator(
    provider,
    asset
  )({
    '@type': 'View',
    'object': {
      '@id': `sdrn:${provider}:content:player:element:error:${errorCode}`,
      '@type': 'UIElement',
      'name': 'error view',
      'spt:custom': cleanupObject({
        'spt:assetControl': Object.keys(asset.additional?.access ?? {})[0],
        'spt:streamType': asset.streamType,
        'spt:streamConfiguration': asset.streamConfiguration?.properties,
        'spt:streamUrl': asset.streamUrls?.hls,
      }),
    },
  });
};

export const createClickTrackEvent = (
  provider: StreamProvider | 'brandstudio',
  asset: Partial<Asset>,
  button: {
    title: string;
    elementType?: string;
    elementIdentifier?: string;
    intent?: string;
  },
  target?: ButtonTarget,
  sptCustom?: Partial<Record<string, unknown>>,
  objectType?: 'ad' | 'asset',
  adId?: string
) => {
  return createOriginDecorator(
    provider,
    asset
  )(
    createButtonClickEvent({
      provider: asset.provider ?? provider,
      target,
      object: {
        id: button.elementIdentifier,
        assetId: asset.id,
        adId,
        name: button.title,
        elementType: button.elementType,
        objectType,
      },
      sptCustom,
      intent: button.intent,
    })
  );
};

const createCastTrackEvent = ({
  provider,
  asset,
  name,
  intent,
}: {
  provider: StreamProvider | 'brandstudio';
  asset: Partial<Asset>;
  intent: string;
  name: string;
}) => {
  const event = createButtonClickEvent({
    provider: asset.provider ?? provider,
    target: {
      '@id': `sdrn:${asset.provider ?? provider}:mediaasset:${asset.id}`,
      '@type': 'MediaAsset',
      'name': asset.title,
    },
    object: {
      assetId: asset.id,
      name: `${name}:${asset.id}`,
      elementType: 'Player',
    },
    intent,
  });

  return createOriginDecorator(provider, asset)(event);
};

export const createChromecastSessionStartingTrackEvent = ({
  provider,
  asset = {},
}: {
  provider: StreamProvider | 'brandstudio';
  asset?: Partial<Asset>;
}) => {
  return createCastTrackEvent({
    provider,
    asset,
    name: 'chromecastConnecting',
    intent: 'Attempt',
  });
};

export const createCastSessionStartedTrackEvent = ({
  provider,
  asset = {},
  castingType,
}: {
  provider: StreamProvider | 'brandstudio';
  asset?: Partial<Asset>;
  castingType: CastingType;
}) => {
  return createCastTrackEvent({
    provider,
    asset,
    name: `${castingType}Start`,
    intent: 'Play',
  });
};

export const createCastSessionEndedTrackEvent = ({
  provider,
  asset = {},
  castingType,
  duration,
}: {
  provider: StreamProvider | 'brandstudio';
  asset?: Partial<Asset>;
  castingType: CastingType;
  duration: number;
}) => {
  const event = createCastTrackEvent({
    provider,
    asset,
    name: `${castingType}Stop`,
    intent: 'Stop',
  });

  return {
    ...event,
    duration,
  };
};

export const createFloatingTrackEvent = ({
  provider,
  asset,
  isFloating,
  isAd,
  adId,
  position,
  duration,
}: {
  provider: StreamProvider | 'brandstudio';
  asset: Partial<Asset>;
  isFloating: boolean;
  isAd: boolean;
  adId?: string;
  position: number;
  duration: number;
}) => {
  const assetProvider = asset.provider ?? provider;

  return createOriginDecorator(
    provider,
    asset
  )({
    '@type': 'Engagement',
    'intent': isFloating ? 'EnterSticky' : 'ExitSticky',
    'position': position,
    'duration': duration,
    'object': {
      '@id': isAd
        ? `sdrn:${assetProvider}:mediaad:${adId}`
        : `sdrn:${assetProvider}:mediaasset:${asset.id}`,
      '@type': isAd ? 'MediaAd' : 'MediaAsset',
      'mediaAssetId': asset.id,
      'elementType': 'sticky',
      'name': 'sticky',
    },
  });
};
